import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import { faMinusCircle } from '@fortawesome/pro-regular-svg-icons';
import IconButton from '@components/IconButton';
import SearchableTable from '@components/SearchableTable';
import { ProfilePictureAvatar } from '@components/ProfilePicture';
import EditMemberModal from './EditMemberModal';
import { selectRules } from '@pathways/redux/rules/reducers';
import { fetchRules } from '@pathways/redux/rules/actions';
import {
  createCareTeamRoleMember,
  editCareTeamRoleMember,
  deleteCareTeamRoleMember,
} from '@redux/careTeam/actions';
import './style.less';

function CareTeamMembers({ appUserPathwayCareTeam, appUserPathway, appUserId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([]);
  const [editingRole, setEditingRole] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [rulesLoading, rulesFromSelector] = useSelector(selectRules);

  useEffect(() => {
    if (!rulesLoading && rulesFromSelector.length === 0) {
      dispatch(fetchRules());
    }
  }, [rulesLoading, rulesFromSelector, dispatch]);

  const currentRoles = useMemo(() => {
    return appUserPathwayCareTeam || [];
  }, [appUserPathwayCareTeam]);

  const currentMembers = useMemo(() => {
    if (!appUserPathwayCareTeam) return [];

    return appUserPathwayCareTeam.flatMap(role =>
      role.members.map(member => ({
        ...member,
        role: role.id,
      })),
    );
  }, [appUserPathwayCareTeam]);

  const dataSource = useMemo(() => {
    const rows = [];

    currentRoles.forEach(role => {
      const roleMembers = currentMembers.filter(member => member.role === role.id);

      if (roleMembers.length === 0) {
        rows.push({
          key: role.id,
          roleId: role.id,
          roleName: role.name,
          roleDescription: role.description,
          rolePermissions: role.permissions,
          memberName: '',
          memberEmail: '',
          memberPhone: '',
          memberBusinessName: '',
          memberId: null,
          hasMember: false,
          profilePictureUrl: null,
        });
        return;
      }

      roleMembers.forEach(member => {
        rows.push({
          key: member.id,
          roleId: role.id,
          roleName: role.name,
          roleDescription: role.description,
          rolePermissions: role.permissions,
          memberName: member.name || '',
          memberEmail: member.email || '',
          memberPhone: member.phone || '',
          memberBusinessName: member.businessName || '',
          memberId: member.id,
          hasMember: true,
          profilePictureUrl: member.profilePictureUrl ?? null,
        });
      });
    });

    return rows;
  }, [currentRoles, currentMembers]);

  const columns = [
    {
      title: t('patients:Pathways.careTeam.columns.role'),
      dataIndex: 'roleName',
      key: 'roleName',
      width: 150,
      render: text => <p className="care-team-members-role-name">{text}</p>,
    },
    {
      title: t('patients:Pathways.careTeam.columns.name'),
      dataIndex: 'memberName',
      key: 'memberName',
      width: 200,
      render: (text, row) => (
        <span className="care-team-members-name-cell">
          <ProfilePictureAvatar
            url={row.profilePictureUrl}
            displayName={row.memberName}
            size={32}
          />
          <span>{text}</span>
        </span>
      ),
    },
    {
      title: t('patients:Pathways.careTeam.columns.email'),
      dataIndex: 'memberEmail',
      key: 'memberEmail',
      width: 200,
    },
    {
      title: t('patients:Pathways.careTeam.columns.phone'),
      dataIndex: 'memberPhone',
      key: 'memberPhone',
      width: 150,
    },
    {
      title: t('patients:Pathways.careTeam.columns.businessName'),
      dataIndex: 'memberBusinessName',
      key: 'memberBusinessName',
      width: 200,
    },
  ];

  const filterFunc = useCallback((row, comparator) => {
    const searchableFields = [
      row.roleName,
      row.memberName,
      row.memberEmail,
      row.memberPhone,
      row.memberBusinessName,
    ];
    return searchableFields.some(field => comparator(field || ''));
  }, []);

  const handleRowClick = useCallback(
    row => {
      const role = currentRoles.find(r => r.id === row.roleId);
      const member = row.memberId
        ? currentMembers.find(m => m.id === row.memberId) || null
        : null;

      setEditingRole({
        ...role,
        member,
      });
      setModalVisible(true);
    },
    [currentRoles, currentMembers],
  );

  const handleSaveMember = useCallback(
    memberData => {
      const existingMember = editingRole?.member;
      const appUserPathwayId = appUserPathway?.id?.toString();

      if (existingMember?.id) {
        dispatch(
          editCareTeamRoleMember(
            appUserId,
            editingRole.id,
            existingMember.id,
            {
              name: memberData.name,
              email: memberData.email,
              phone: memberData.phone,
              businessName: memberData.businessName,
              staffUserId: memberData.staffUserId,
              staffOrganisationId: memberData.staffOrganisationId,
            },
            appUserPathwayId,
          ),
        );
      } else {
        dispatch(
          createCareTeamRoleMember(
            appUserId,
            editingRole.id,
            {
              name: memberData.name,
              email: memberData.email,
              phone: memberData.phone,
              businessName: memberData.businessName,
              staffUserId: memberData.staffUserId,
              staffOrganisationId: memberData.staffOrganisationId,
            },
            appUserPathwayId,
          ),
        );
      }

      setModalVisible(false);
      setEditingRole(null);
    },
    [dispatch, appUserId, appUserPathway, editingRole],
  );

  const handleRemoveMember = useCallback(() => {
    const appUserPathwayId = appUserPathway?.id?.toString();
    selectedRows.forEach(row => {
      if (row.hasMember && row.memberId) {
        dispatch(deleteCareTeamRoleMember(appUserId, row.roleId, row.memberId, appUserPathwayId));
      }
    });
    setSelectedRows([]);
  }, [dispatch, appUserId, appUserPathway, selectedRows]);

  const handleDeleteMember = useCallback(role => {
    setModalVisible(false);
    setEditingRole(null);
    setMemberToDelete(role);
  }, []);

  const confirmDeleteMember = useCallback(() => {
    if (memberToDelete?.id && memberToDelete?.member?.id) {
      const appUserPathwayId = appUserPathway?.id?.toString();
      dispatch(
        deleteCareTeamRoleMember(
          appUserId,
          memberToDelete.id,
          memberToDelete.member.id,
          appUserPathwayId,
        ),
      );
    }
    setMemberToDelete(null);
  }, [dispatch, appUserId, appUserPathway, memberToDelete]);

  const canRemove = useMemo(() => {
    return selectedRows.length > 0 && selectedRows.some(row => row.hasMember);
  }, [selectedRows]);

  if (!appUserPathwayCareTeam) return null;

  return (
    <>
      <SearchableTable
        className="care-team-members-table"
        columns={columns}
        dataSource={dataSource}
        extra={
          <div className="care-team-members-actions">
            <IconButton
              disabled={!canRemove}
              icon={faMinusCircle}
              tooltip={t('patients:Pathways.careTeam.buttons.remove')}
              onClick={handleRemoveMember}
            />
          </div>
        }
        filterFunc={filterFunc}
        loading={false}
        pagination={false}
        rowKey="key"
        title={t('patients:Pathways.careTeam.title')}
        onRowClick={handleRowClick}
        onRowSelectionChange={(_, rows) => setSelectedRows(rows)}
      />
      <EditMemberModal
        visible={modalVisible}
        role={editingRole}
        onSave={handleSaveMember}
        onDelete={handleDeleteMember}
        onCancel={() => {
          setModalVisible(false);
          setEditingRole(null);
        }}
        appUserPathway={appUserPathway}
      />
      <Modal
        title={t('patients:Pathways.careTeam.deleteConfirm.title')}
        visible={!!memberToDelete}
        onOk={confirmDeleteMember}
        onCancel={() => setMemberToDelete(null)}
        okText={t('patients:Pathways.careTeam.deleteConfirm.ok')}
        cancelText={t('patients:Pathways.careTeam.deleteConfirm.cancel')}
        okButtonProps={{ danger: true }}
      >
        <p>
          {t('patients:Pathways.careTeam.deleteConfirm.message', {
            name: memberToDelete?.member?.name || '',
          })}
        </p>
      </Modal>
    </>
  );
}

export default CareTeamMembers;
